import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadgeDot from "components/MDBadgeDot";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import Reports from "./Reports";

import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/actions/dashboard";
import * as actionTypes from "redux/constants";
import InputFilters from "./FiltersInput";
import moment from "moment";

const MODULE_LABELS = [
  "Reminiscence",
  "Language",
  "Visual_Tools",
  "Spatial_Time",
  "Decision_Making",
  "Daily_Tasks",
];

const emptyBarChart = {
  labels: MODULE_LABELS,
  datasets: [{ label: "Activity Level by Cognitive Modules", color: "dark", data: [] }],
};

// trendInsight() — local fallback when backend insight is absent.
// Backend (PlayReportManager.GetBehaviouralSummary) is the primary source.
function trendInsight(items) {
  if (!items || !items.length) return null;

  // Reads both PascalCase (summary API) and camelCase (direct API)
  const getField = (r, pascal, camel) => r[pascal] ?? r[camel] ?? 0;
  const avg = (pascal, camel) =>
    parseFloat((items.reduce((a, r) => a + getField(r, pascal, camel), 0) / items.length).toFixed(1));

  const sleep    = avg("SleepPct",    "sleepPct");
  const food     = avg("FoodPct",     "foodPct");
  const mood     = avg("MoodPct",     "moodPct");
  const activity = avg("ActivityPct", "activityPct");

  const scoreLabel = (v) => (v >= 75 ? "Excellent" : v >= 55 ? "Good" : v >= 35 ? "Fair" : "Needs Attention");
  const scoreColor = (v) => (v >= 75 ? "#4CAF50" : v >= 55 ? "#1976d2" : v >= 35 ? "#FF9800" : "#f44336");

  const tips = {
    sleep: {
      high: "Sleep quality is excellent. Consistent sleep patterns strongly support memory consolidation and mood regulation.",
      mid:  "Sleep is moderate. Aim for 7–9 hours and maintain a regular bedtime to improve cognitive recovery overnight.",
      fair: "Sleep quality is below average. Poor sleep is one of the biggest drivers of cognitive decline — prioritise rest.",
      low:  "Sleep is critically low. This will directly impact memory, attention, and emotional balance. Address sleep hygiene urgently.",
    },
    food: {
      high: "Nutritional habits are excellent. A balanced diet fuels the brain and supports sustained cognitive energy.",
      mid:  "Food scores are moderate. Increasing intake of omega-3s, leafy greens, and hydration will noticeably sharpen focus.",
      fair: "Diet quality is below average. Consider tracking meals and reducing processed food — nutrition directly impacts brain health.",
      low:  "Nutritional intake is low. Poor diet is a significant risk factor for cognitive decline and low energy levels.",
    },
    mood: {
      high: "Mood is very positive. Emotional well-being is a strong predictor of cognitive engagement and learning ability.",
      mid:  "Mood is generally stable. Social interaction, light exercise, and mindfulness can help lift it further.",
      fair: "Mood has been below average. Identify stressors and consider relaxation techniques or social activities.",
      low:  "Mood is persistently low. This can significantly impair motivation and cognitive performance. Consider seeking support.",
    },
    activity: {
      high: "Physical activity is excellent. Exercise increases blood flow to the brain and is one of the most effective cognitive boosters.",
      mid:  "Activity level is moderate. Adding 20 minutes of walking daily can measurably improve brain health over weeks.",
      fair: "Physical activity is below average. Even light movement — stretching or short walks — has measurable cognitive benefits.",
      low:  "Activity is very low. Sedentary behaviour accelerates cognitive decline. Start with small, achievable movement goals.",
    },
  };

  const pick = (v, t) => (v >= 75 ? t.high : v >= 55 ? t.mid : v >= 35 ? t.fair : t.low);
  const all = [sleep, food, mood, activity];
  const overallAvg = parseFloat((all.reduce((a, b) => a + b, 0) / 4).toFixed(1));
  let overall = "";
  if (overallAvg >= 75) overall = "Overall behavioural health is excellent. All key lifestyle pillars are well-maintained.";
  else if (overallAvg >= 55) overall = "Behavioural health is in a good range. Small improvements across lifestyle habits will compound over time.";
  else if (overallAvg >= 35) overall = "Behavioural health is moderate. Focus on the lower-scoring areas below for the biggest cognitive gains.";
  else overall = "Behavioural health needs attention across multiple areas. Building better lifestyle habits is the highest-impact action you can take.";

  return {
    overall,
    overallAvg,
    channels: [
      { label: "Sleep",    value: sleep,    color: scoreColor(sleep),    levelLabel: scoreLabel(sleep),    tip: pick(sleep,    tips.sleep),    icon: "bedtime" },
      { label: "Food",     value: food,     color: scoreColor(food),     levelLabel: scoreLabel(food),     tip: pick(food,     tips.food),     icon: "restaurant" },
      { label: "Mood",     value: mood,     color: scoreColor(mood),     levelLabel: scoreLabel(mood),     tip: pick(mood,     tips.mood),     icon: "sentiment_satisfied" },
      { label: "Activity", value: activity, color: scoreColor(activity), levelLabel: scoreLabel(activity), tip: pick(activity, tips.activity), icon: "directions_run" },
    ],
  };
}

// ─── Static insight rules for modules ────────────────────────────────────────

const MODULE_INFO = {
  Reminiscence: {
    label: "Reminiscence",
    icon: "auto_stories",
    color: "#7c4dff",
    high: "Excellent engagement with personal memories. Reminiscence activities strengthen long-term recall and emotional well-being.",
    mid: "Good participation. Regular reminiscence sessions help anchor identity and reduce cognitive decline.",
    low: "Low activity. Reminiscence games are among the most enjoyable — try adding one short session per day.",
  },
  Language: {
    label: "Language",
    icon: "translate",
    color: "#1976d2",
    high: "Strong language engagement. This directly supports verbal fluency and communication skills.",
    mid: "Moderate language activity. Increasing word games will improve verbal processing speed.",
    low: "Language needs more attention. Even 5 minutes of word-search daily builds significant gains.",
  },
  Visual_Tools: {
    label: "Visual Tools",
    icon: "visibility",
    color: "#0097a7",
    high: "Great visual processing activity. This supports spatial reasoning and pattern recognition.",
    mid: "Decent visual engagement. More visual exercises will sharpen perceptual accuracy.",
    low: "Low visual activity. Visual tools are key for maintaining spatial awareness — try increasing sessions.",
  },
  Spatial_Time: {
    label: "Spatial & Time",
    icon: "schedule",
    color: "#388e3c",
    high: "Excellent orientation and time awareness training. Keeps daily routines and schedules grounded.",
    mid: "Moderate spatial-time engagement. Calendar and clock exercises reinforce daily orientation.",
    low: "Low engagement. Spatial-time exercises help maintain awareness of place, date and routine.",
  },
  Decision_Making: {
    label: "Decision Making",
    icon: "psychology",
    color: "#f57c00",
    high: "Strong decision-making activity. Executive function and planning skills are well exercised.",
    mid: "Good effort. More complex decision-making tasks will further strengthen executive function.",
    low: "Needs improvement. Decision-making games build critical thinking and problem-solving capacity.",
  },
  Daily_Tasks: {
    label: "Daily Tasks",
    icon: "checklist",
    color: "#c62828",
    high: "Excellent daily task engagement. Practical cognitive skills are being well maintained.",
    mid: "Good participation in daily tasks. These simulate real-life skills and support independence.",
    low: "Low activity. Daily task games are essential for maintaining independence in everyday life.",
  },
};

function moduleInsight(label, score) {
  const info = MODULE_INFO[label] || {
    label,
    icon: "bar_chart",
    color: "#757575",
    high: "Strong engagement in this module.",
    mid: "Moderate activity. Increase sessions for better results.",
    low: "Low activity. Regular practice recommended.",
  };
  const text = score >= 70 ? info.high : score >= 40 ? info.mid : info.low;
  const level = score >= 70 ? "Excellent" : score >= 40 ? "Good" : "Needs Attention";
  const levelColor = score >= 70 ? "#4CAF50" : score >= 40 ? "#FF9800" : "#f44336";
  return { ...info, text, level, levelColor };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BehaviouralReport() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const dashboard = useSelector((state) => state.dashboard);

  const [userID, setuserID] = useState(undefined);
  const [report, setReport] = useState({ id: 3, name: "Last Month" });
  const [chartData, setChartData] = useState(emptyBarChart);
  const [moduleScores, setModuleScores] = useState({});
  const [startDate, setStartDate] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [reportTimeLine, setreportTimeLine] = useState({
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const userId = auth?.user?.userId || auth?.user?.id;

  const dispatchAll = (uid, dates) => {
    dispatch(actions.getExerciseActivity(uid, dates));
    dispatch(actions.getMeditationActivity(uid, dates));
    dispatch(actions.getPlayReportById(uid, dates));
    dispatch(actions.getModulePlayReportById(uid, dates));
    dispatch(actions.getBehaviouralReport(uid, dates));
  };

  useEffect(() => {
    const defaultDates = {
      fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    };
    if (userId) dispatchAll(userId, defaultDates);
  }, []);

  useEffect(() => {
    const tempChartData = { ...emptyBarChart, datasets: [{ ...emptyBarChart.datasets[0], data: [] }] };
    const scores = {};
    const playReportList = dashboard?.modulePlayReportById?.playReport || [];
    MODULE_LABELS.forEach((label, i) => {
      const entry = playReportList.find((m) => m.module === label);
      const val = entry ? entry.periodUserActivity : 0;
      tempChartData.datasets[0].data[i] = val;
      scores[label] = val;
    });
    setChartData(tempChartData);
    setModuleScores(scores);
  }, [dashboard.modulePlayReportById]);

  // Normalize behaviouralReport: summary API returns { items, insight }; direct API now also returns BehaviouralSummaryDto
  const rawBeh = dashboard.behaviouralReport;
  const behItems = Array.isArray(rawBeh) ? rawBeh : (rawBeh?.items || rawBeh?.Items || []);
  const behInsight = Array.isArray(rawBeh) ? null : (rawBeh?.insight || rawBeh?.Insight || null);
  const behLoading = dashboard.behaviouralReportLoading;

  const exercisePct = dashboard.exerciseActivity?.periodUserActivity ?? null;
  const meditationPct = dashboard.meditationActivity?.periodUserActivity ?? null;
  const playPct = dashboard.playReportById?.playReport?.periodUserActivity ?? null;
  const behaviouralPct = dashboard.modulePlayReportById?.playReport
    ?.find((m) => m.module === "Behavioural")?.periodUserActivity ?? null;

  // ×100 scaling moved to backend — use pre-computed Pct fields from BehaviouralReportDto
  const overallDailyScore = behItems.length
    ? parseFloat(
        (behItems.reduce((acc, r) => acc + (r.OverallPct ?? r.overallPct ?? 0), 0) / behItems.length).toFixed(1)
      )
    : 0;

  const trendChart = {
    labels: behItems.map((r) => moment(r.date || r.Date).format("DD MMM")),
    datasets: [
      { label: "Sleep",    color: "info",    data: behItems.map((r) => +(r.SleepPct    ?? r.sleepPct    ?? 0).toFixed(1)) },
      { label: "Food",     color: "success", data: behItems.map((r) => +(r.FoodPct     ?? r.foodPct     ?? 0).toFixed(1)) },
      { label: "Mood",     color: "warning", data: behItems.map((r) => +(r.MoodPct     ?? r.moodPct     ?? 0).toFixed(1)) },
      { label: "Activity", color: "error",   data: behItems.map((r) => +(r.ActivityPct ?? r.activityPct ?? 0).toFixed(1)) },
    ],
  };

  const emptyTrend = {
    labels: [],
    datasets: [
      { label: "Sleep", color: "info", data: [] },
      { label: "Food", color: "success", data: [] },
      { label: "Mood", color: "warning", data: [] },
      { label: "Activity", color: "error", data: [] },
    ],
  };

  const getDates = (name) => {
    switch (name) {
      case "last year":  return { fromDate: moment().subtract(365, "days").format("YYYY-MM-DD"), toDate: moment().format("YYYY-MM-DD") };
      case "last month": return { fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),  toDate: moment().format("YYYY-MM-DD") };
      case "last week":  return { fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),   toDate: moment().format("YYYY-MM-DD") };
      case "today":      return { fromDate: moment().format("YYYY-MM-DD"),                       toDate: moment().format("YYYY-MM-DD") };
      default:           return null;
    }
  };

  const handleReport = (name, value) => {
    setReport(value);
    const dates = getDates(value.name.toLowerCase().trim());
    if (dates) {
      setreportTimeLine(dates);
      const uid = (auth.user?.role === "user" || auth.user?.role === "patient") ? userId : userID;
      if (uid) dispatchAll(uid, dates);
    }
  };

  const handleChange = (e) => {
    const clone = { ...reportTimeLine };
    if (e.target.name === "startDate") { setStartDate(e.target.value); clone.fromDate = e.target.value; }
    else { setEndDate(e.target.value); clone.toDate = e.target.value; }
    setreportTimeLine(clone);
  };

  const handleUserIDChange = (e) => setuserID(e.target.value);

  const handleSearch = () => {
    const uid = userID?.trim();
    if (uid) dispatchAll(uid, reportTimeLine);
  };

  const handleSubmitReport = () => {
    const uid = (auth.user?.role === "user" || auth.user?.role === "patient") ? userId : userID;
    if (uid) dispatchAll(uid, reportTimeLine);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {actionTypes.ADVANCED_SECTION.includes(auth.user?.role) && (
        <Grid container>
          <Grid item xs={12}>
            <MDBox mt={3}><InputFilters handleUserIDChange={handleUserIDChange} handleSearch={handleSearch} /></MDBox>
          </Grid>
        </Grid>
      )}

      <Grid container>
        <Grid item xs={12}>
          <MDBox mt={4}>
            <Reports
              dashboard={dashboard} report={report} handleReport={handleReport}
              startDate={startDate} endDate={endDate}
              handleChange={handleChange} handleSubmitReport={handleSubmitReport}
            />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox py={3}>

        {/* ── Stat cards — all 4 in one line ────────────────────────────── */}
        <MDBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <DefaultStatisticsCard title="Exercises Activity"
                count={exercisePct != null ? `${exercisePct}%` : "0%"} percentage={{ color: "success" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DefaultStatisticsCard title="Meditation Activity"
                count={meditationPct != null ? `${meditationPct}%` : "0%"} percentage={{ color: "secondary" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DefaultStatisticsCard title="Behavioural Activity"
                count={behaviouralPct != null ? `${behaviouralPct}%` : "0%"} percentage={{ color: "secondary" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DefaultStatisticsCard title="Play Activity"
                count={playPct != null ? `${playPct}%` : "0%"} percentage={{ color: "info" }} />
            </Grid>
          </Grid>
        </MDBox>

        {/* ── Behavioural Health Score donut + Behavioural Trend ─────────────────── */}
        <MDBox mb={3}>
          <Grid container spacing={3} alignItems="stretch">
            {/* Behavioural Health Score Donut */}
            <Grid item xs={12} lg={4}>
              <DefaultDoughnutChart
                icon={{ color: "info", component: "favorite" }}
                title="Behavioural Health Score"
                description={`Average out of 100 — ${report?.name}`}
                chart={{
                  labels: ["Score", "Remaining"],
                  datasets: {
                    label: "Behavioural Health Score",
                    backgroundColors: [
                      overallDailyScore >= 75 ? "success" : overallDailyScore >= 50 ? "warning" : "error",
                      "light",
                    ],
                    data: [overallDailyScore, parseFloat((100 - overallDailyScore).toFixed(1))],
                  },
                }}
              />
            </Grid>

            {/* Behavioural Trend — increased height */}
            <Grid item xs={12} lg={8}>
              <DefaultLineChart
                title="Behavioural Trend"
                height="22rem"
                description={
                  <MDBox display="flex" ml={-1}>
                    <MDBadgeDot color="info"    size="sm" badgeContent="Sleep" />
                    <MDBadgeDot color="success" size="sm" badgeContent="Food" />
                    <MDBadgeDot color="warning" size="sm" badgeContent="Mood" />
                    <MDBadgeDot color="error"   size="sm" badgeContent="Activity" />
                  </MDBox>
                }
                chart={trendChart.labels.length > 0 ? trendChart : emptyTrend}
              />

            </Grid>
          </Grid>
        </MDBox>

        {/* ── Behavioural Trend Summary — always computed from behItems ── */}
        {behItems.length > 0 && (() => {
          const resolved = trendInsight(behItems);
          if (!resolved) return null;
          const channels = resolved.channels || resolved.Channels || [];
          const overall = resolved.overall || resolved.Overall || "";
          const overallAvg = resolved.overallAvg ?? resolved.OverallAvg ?? null;
          if (!overall && channels.length === 0) return null;
          return (
            <MDBox mb={3}>
              <Card>
                <MDBox p={2} display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "warning.main" }}>insights</Icon>
                  <MDTypography variant="h6">Behavioural Trend Summary</MDTypography>
                  <MDTypography variant="caption" color="text" ml={1}>— {report?.name}</MDTypography>
                </MDBox>
                {/* Overall */}
                {overall && (
                  <MDBox mx={2} mb={2} px={2} py={1.5} borderRadius="lg"
                    sx={{ background: "rgba(25,118,210,0.06)", borderLeft: "4px solid #1976d2" }}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Icon sx={{ color: "#1976d2", fontSize: "1rem !important" }}>psychology</Icon>
                      <MDTypography variant="button" fontWeight="bold">
                        Overall Behavioural Health{overallAvg != null ? ` — ${overallAvg}%` : ""}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body2" color="text">{overall}</MDTypography>
                  </MDBox>
                )}
                {/* Per-channel */}
                {channels.length > 0 && (
                  <Grid container spacing={2} px={2} pb={2}>
                    {channels.map((ch) => {
                      const label = ch.label || ch.Label || "";
                      const value = ch.value ?? ch.Value ?? 0;
                      const color = ch.color || ch.Color || "#757575";
                      const levelLabel = ch.levelLabel || ch.LevelLabel || "";
                      const tip = ch.tip || ch.Tip || "";
                      const icon = ch.icon || ch.Icon || "bar_chart";
                      return (
                        <Grid key={label} item xs={12} sm={6} md={3}>
                          <MDBox px={2} py={1.5} borderRadius="lg"
                            sx={{ background: "rgba(0,0,0,0.03)", borderLeft: `4px solid ${color}`, height: "100%" }}>
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                <Icon sx={{ fontSize: "1rem !important", color }}>{icon}</Icon>
                                <MDTypography variant="button" fontWeight="bold">{label}</MDTypography>
                              </MDBox>
                              <MDBox textAlign="right">
                                <MDTypography variant="button" fontWeight="bold" sx={{ color }}>
                                  {value}%
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color, display: "block", lineHeight: 1 }}>
                                  {levelLabel}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDTypography variant="caption" color="text">{tip}</MDTypography>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Card>
            </MDBox>
          );
        })()}

        {/* ── Activity by Modules + static insight panel ─────────────────── */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            {/* Bar chart */}
            <Grid item xs={12} lg={7}>
              <HorizontalBarChart title="Activity by Modules" chart={chartData} />
            </Grid>

            {/* Insight panel */}
            <Grid item xs={12} lg={5}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2} display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "info.main" }}>psychology</Icon>
                  <MDTypography variant="h6">Module Insights</MDTypography>
                </MDBox>
                <MDBox px={2} pb={2} sx={{ overflowY: "auto", maxHeight: "22rem" }}>
                  {MODULE_LABELS.map((label) => {
                    const score = moduleScores[label] ?? 0;
                    const { label: displayLabel, icon, levelColor, level, text } = moduleInsight(label, score);
                    return (
                      <MDBox
                        key={label} mb={1.5} px={2} py={1.5} borderRadius="lg"
                        sx={{ background: "rgba(0,0,0,0.03)", borderLeft: `4px solid ${levelColor}` }}
                      >
                        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                          <MDBox display="flex" alignItems="center" gap={0.75}>
                            <Icon sx={{ fontSize: "1.1rem !important", color: levelColor }}>{icon}</Icon>
                            <MDTypography variant="button" fontWeight="bold">{displayLabel}</MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: levelColor }}>
                              {score > 0 ? `${score}%` : "—"}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: levelColor, fontWeight: 600 }}>
                              {level}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDTypography variant="caption" color="text">{text}</MDTypography>
                      </MDBox>
                    );
                  })}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

      </MDBox>
    </DashboardLayout>
  );
}
